import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const exomeGenomeSequencing: ContentBlock<"procedure"> = {
  id: makeId("proc-exome-genome-sequencing"),
  kind: "procedure",
  tags: ["genetic-screening", "both-partners", "lab"],
  title: "Exome or genome sequencing",
  subtitle: "Comprehensive genetic analysis for rare or complex conditions",
  body: {
    procedureName: "Exome or genome sequencing",
    category: "diagnostic",
    description:
      "Advanced genetic testing that analyzes a large portion of your DNA to identify rare mutations that standard carrier panels may miss. Whole exome sequencing reads the protein-coding regions of all genes, while whole genome sequencing reads the entire DNA sequence.",
    howItWorks:
      "A blood sample is drawn and DNA is extracted. The sequencing machine reads thousands of genes simultaneously, comparing your DNA to a reference sequence to identify variants. Bioinformatics software and geneticists analyze the results, filtering for variants that are known or suspected to cause disease. The process is more comprehensive than targeted carrier panels and can uncover rare genetic conditions. A genetic counselor helps interpret which findings are clinically significant.",
    duration: "5–10 minutes for the blood draw; results in 4–12 weeks",
    anesthesia: "none",
    whatToExpect: [
      "A standard blood draw, same as any lab test",
      "Results take longer than other genetic tests (4–12 weeks) due to the volume of data analyzed",
      "You will meet with a genetic counselor to review results",
      "Some results may be uncertain (variants of unknown significance) and require follow-up",
      "Findings may inform whether PGT or other interventions are recommended",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Exome or genome sequencing commonly takes 4-12 weeks because analysis and interpretation are complex.",
      },
      {
        label: "What you learn",
        detail:
          "The report may identify pathogenic variants, carrier findings, incidental findings, or variants of uncertain significance.",
      },
      {
        label: "Follow-up",
        detail:
          "A genetic counselor should review what is actionable, what is uncertain, and whether partner testing or PGT is relevant.",
      },
    ],
    prepInstructions: [
      "No fasting or special preparation required",
      "Bring a detailed family health history",
      "Consider meeting with a genetic counselor before testing to understand what results may reveal",
      "Both partners may be tested depending on clinical indication",
    ],
    risks: [
      "No physical risks beyond a blood draw",
      "May reveal unexpected findings (incidental findings) unrelated to fertility",
      "Variants of unknown significance can create uncertainty, but genetic counseling helps navigate this",
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Séquençage de l'exome ou du génome",
      description:
        "Test génétique avancé analysant une grande partie de votre ADN pour identifier des mutations rares que les panels classiques peuvent manquer. Le séquençage de l'exome complet lit les régions codantes de tous les gènes, tandis que le séquençage du génome lit l'ensemble de la séquence d'ADN.",
      howItWorks:
        "Une prise de sang est effectuée et l'ADN est extrait. Le séquenceur lit simultanément des milliers de gènes et compare votre ADN à une séquence de référence pour identifier les variants. Des outils bio-informatiques et des généticiens analysent les résultats en filtrant les variants connus ou suspectés de causer une pathologie. La démarche est plus complète qu'un panel ciblé et peut révéler des maladies génétiques rares. Un conseiller en génétique aide à interpréter la signification clinique des résultats.",
      duration:
        "5 à 10 minutes pour la prise de sang ; résultats sous 4 à 12 semaines",
      whatToExpect: [
        "Prise de sang standard, identique à tout examen biologique",
        "Les résultats prennent plus de temps que les autres tests génétiques (4 à 12 semaines) en raison du volume de données analysées",
        "Vous rencontrerez un conseiller en génétique pour analyser les résultats",
        "Certains résultats peuvent être incertains (variants de signification inconnue) et nécessiter un suivi",
        "Les résultats peuvent orienter l'indication d'un DPI ou d'autres interventions",
      ],
      prepInstructions: [
        "Aucun jeûne ni préparation particulière",
        "Apportez un historique familial détaillé",
        "Envisagez une consultation préalable avec un conseiller en génétique pour comprendre ce que les résultats peuvent révéler",
        "Les deux partenaires peuvent être testés selon l'indication clinique",
      ],
      risks: [
        "Aucun risque physique au-delà d'une prise de sang",
        "Peut révéler des résultats inattendus (découvertes incidentes) sans lien avec la fertilité",
        "Les variants de signification inconnue peuvent générer de l'incertitude ; le conseil génétique permet d'y faire face",
      ],
    },
  },
  sources: [
    {
      title:
        "Screening for autosomal recessive and X-linked conditions during pregnancy and preconception: a practice resource of the ACMG",
      authors: "Gregg AR et al.",
      journal: "Genetics in Medicine",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/34914171/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(exomeGenomeSequencing);
